/* ============================================================
   AM2T — global-newsletter-popup.js
   sections/global-newsletter-popup.liquid
   - Opens after the configured delay unless the visitor subscribed
     or dismissed it recently (localStorage, wrapped in try/catch so
     private browsing never breaks the page).
   - Accessible dialog: focus moves in, Tab is trapped, the secondary
     button or Escape close it (no × and no backdrop click, on purpose),
     focus returns where it was.
   ============================================================ */

(() => {
  'use strict';

  const store = {
    get(key) { try { return localStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch { /* private mode */ } }
  };

  const init = (root) => {
    const popup = root.querySelector ? root.querySelector('#am2t-popup-overlay') : null;
    if (!popup || popup.dataset.ready === 'true') return;
    popup.dataset.ready = 'true';

    const dialog = popup.querySelector('.am2t-popup');
    const delay = parseInt(popup.dataset.delay, 10) || 0;
    const dismissDays = parseInt(popup.dataset.dismissDays, 10) || 7;
    const designMode = popup.hasAttribute('data-design-mode');
    let lastFocus = null;

    const focusables = () =>
      dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled])');

    const onKeydown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const open = () => {
      lastFocus = document.activeElement;
      popup.classList.add('is-visible');
      popup.setAttribute('aria-hidden', 'false');
      document.body.classList.add('am2t-popup-open');
      document.addEventListener('keydown', onKeydown);
      const input = popup.querySelector('#am2t-email');
      setTimeout(() => (input || focusables()[0])?.focus(), 50);
    };

    const close = () => {
      popup.classList.remove('is-visible');
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('am2t-popup-open');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    };

    const dismiss = () => {
      close();
      if (!designMode) store.set('am2tPopupDismissedUntil', String(Date.now() + dismissDays * 864e5));
    };

    popup.querySelectorAll('[data-popup-dismiss]').forEach((btn) => btn.addEventListener('click', dismiss));

    // In the theme editor: open when the section is selected, never auto.
    if (designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.contains(popup)) open();
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target.contains(popup)) close();
      });
      return;
    }

    if (store.get('am2tPopupSubscribed') === 'true') return;
    const until = parseInt(store.get('am2tPopupDismissedUntil'), 10);
    if (until && Date.now() < until) return;

    setTimeout(open, delay);
  };

  window.AM2T.onSection(init);
})();
